import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useLogin } from "../hooks/useLogin";
import { useForm } from "react-hook-form";
import { EyeSlashIcon, EyeIcon } from "@heroicons/react/24/outline";
import NavbarSmallWebsite from "../components/NavbarSmallWebsite";
import { Link } from "@tanstack/react-router";
import useTranslation from "../hooks/useTranslation";
type FormValues = {
  username: string;
  password: string;
};

export const Route = createFileRoute("/login")({
  component: LoginComponent,
});

function LoginComponent() {
  const { translate } = useTranslation();
  const { loginMutation } = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>();
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  const handlePasswordReset = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Replace with your password reset logic
    alert(translate("login_reset_sent_alert", { email: resetEmail }));
    setShowForgotModal(false);
  };

  const onSubmit = async (data: { username: string; password: string }) => {
    try {
      await loginMutation.mutateAsync({
        username: data.username,
        password: data.password,
      });
    } catch {
      setError("password", {
        type: "custom",
        message: translate("login_invalid_credentials"),
      });
    }
  };

  return (
    <div className="pt-20 md:min-h-screen w-full flex items-center justify-center bg-slate-50 px-4 font-TikTok">
      <NavbarSmallWebsite />
      <section className="bg-white border border-neutral-200 rounded-2xl p-6 md:p-8 shadow-md w-full md:max-w-md">
        <h2 className="text-xl md:text-2xl font-semibold text-left">
          {translate("login_title")}
        </h2>
        <p className="text-sm font-light mb-8 text-left">
          {translate("login_subtitle")}
        </p>

        <form className="space-y-4 text-left" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="block mb-1 text-sm font-medium">
              {translate("login_username_label")}
            </label>
            <input
              type="text"
              placeholder={translate("login_username_placeholder")}
              className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              {...register("username", { required: true, maxLength: 80 })}
            />
          </div>

          <div className="relative">
            <label className="block mb-1 text-sm font-medium">
              {translate("login_password_label")}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={translate("login_password_placeholder")}
                className="w-full border rounded-lg px-4 py-2 pr-10 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                {...register("password", { required: true })}
              />
              <span
                role="button"
                tabIndex={0}
                onClick={() => setShowPassword((prev) => !prev)}
                onKeyDown={(e) =>
                  e.key === "Enter" && setShowPassword((prev) => !prev)
                }
                className="absolute inset-y-0 right-3 flex items-center text-gray-500 cursor-pointer"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </span>
            </div>
            {errors.password && (
              <span className="text-xs text-red-500">
                {errors.password.message}
              </span>
            )}
          </div>

          {/* Remember me and Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
                className="form-checkbox rounded text-blue-600"
              />
              <span>{translate("login_remember_me")}</span>
            </label>
            <section
              onClick={() => setShowForgotModal(true)}
              className="text-blue-600 hover:underline cursor-pointer"
            >
              {translate("login_forgot_password")}
            </section>
          </div>

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-slate-200"
            disabled={loginMutation.isPending}
          >
            {translate("login_continue_with_username")}
          </button>

          {/* Signup and Home links */}
          <div className="pt-4 flex flex-col md:flex-row justify-between items-center text-sm text-neutral-600 gap-2">
            <p>
              {translate("login_no_account")}{" "}
              <a href="/signup" className="text-blue-600 hover:underline">
                {translate("login_sign_up")}
              </a>
            </p>
            <Link
              to="/app"
              className="text-blue-600 hover:underline"
            >
              {translate("login_back_home")}
            </Link>
          </div>
        </form>
      </section>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-lg">
            <h3 className="text-lg font-semibold mb-2 text-left">
              {translate("login_reset_title")}
            </h3>
            <p className="text-sm mb-4 text-gray-500 text-left">
              {translate("login_reset_description")}
            </p>
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder={translate("login_reset_email_placeholder")}
                className="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                required
              />
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowForgotModal(false)}
                  className="text-sm px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  {translate("login_cancel")}
                </button>
                <button
                  type="submit"
                  className="text-sm px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {translate("login_send_reset_link")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
